// Geolocation detection service for automatic language and currency detection

/**
 * Detects user's country based on geolocation API
 * Falls back to timezone and browser language if geolocation fails
 */
export const detectUserCountry = async () => {
  // Use browser-based detection directly to avoid CORS issues with external APIs
  // This is more reliable and doesn't require external service availability
  return detectFromBrowser();
};

/**
 * Fallback detection using browser APIs
 */
const detectFromBrowser = () => {
  const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  const browserLang = navigator?.language || navigator?.userLanguage;
  
  // Map timezone to country
  const country = getCountryFromTimezone(timezone);
  
  return {
    country,
    countryName: getCountryName(country),
    language: getLanguageFromCountry(country) || browserLang,
    currency: getCurrencyFromCountry(country),
    timezone
  };
};

/**
 * Maps country code to language code
 */
const getLanguageFromCountry = (countryCode) => {
  const countryLanguageMap = {
    'BR': 'pt-BR',
    'US': 'en-US',
    'GB': 'en-GB',
    'CA': 'en-CA',
    'AU': 'en-AU',
    'PT': 'pt-BR',
    'ES': 'es-ES',
    'MX': 'es-MX',
    'AR': 'es-AR',
    'CL': 'es-CL',
    'CO': 'es-CO',
    'PE': 'es-PE',
    'FR': 'fr-FR',
    'DE': 'de-DE',
    'IT': 'it-IT',
    'JP': 'ja-JP',
    'CN': 'zh-CN',
    'IN': 'en-IN',
    'CH': 'de-CH'
  };
  return countryLanguageMap?.[countryCode] || 'pt-BR';
};

/**
 * Maps country code to currency code
 */
const getCurrencyFromCountry = (countryCode) => {
  const countryCurrencyMap = {
    'BR': 'BRL',
    'US': 'USD',
    'GB': 'GBP',
    'CA': 'CAD',
    'AU': 'AUD',
    'PT': 'EUR',
    'ES': 'EUR',
    'FR': 'EUR',
    'DE': 'EUR',
    'IT': 'EUR',
    'MX': 'MXN',
    'AR': 'ARS',
    'CL': 'CLP',
    'CO': 'COP',
    'PE': 'PEN',
    'JP': 'JPY',
    'CN': 'CNY',
    'IN': 'INR',
    'CH': 'CHF'
  };
  return countryCurrencyMap?.[countryCode] || 'BRL';
};

/**
 * Maps timezone to country code
 */
const getCountryFromTimezone = (timezone) => {
  const timezoneCountryMap = {
    'America/Sao_Paulo': 'BR',
    'America/Fortaleza': 'BR',
    'America/Manaus': 'BR',
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Los_Angeles': 'US',
    'America/Toronto': 'CA',
    'America/Vancouver': 'CA',
    'America/Mexico_City': 'MX',
    'America/Argentina/Buenos_Aires': 'AR',
    'America/Santiago': 'CL',
    'America/Bogota': 'CO',
    'America/Lima': 'PE',
    'Europe/London': 'GB',
    'Europe/Paris': 'FR',
    'Europe/Berlin': 'DE',
    'Europe/Rome': 'IT',
    'Europe/Madrid': 'ES',
    'Europe/Lisbon': 'PT',
    'Europe/Zurich': 'CH',
    'Asia/Tokyo': 'JP',
    'Asia/Shanghai': 'CN',
    'Asia/Kolkata': 'IN',
    'Australia/Sydney': 'AU'
  };
  return timezoneCountryMap?.[timezone] || 'BR';
};

/**
 * Gets country name from code
 */
const getCountryName = (countryCode) => {
  const countryNames = {
    'BR': 'Brazil',
    'US': 'United States',
    'GB': 'United Kingdom',
    'CA': 'Canada',
    'AU': 'Australia',
    'PT': 'Portugal',
    'ES': 'Spain',
    'MX': 'Mexico',
    'AR': 'Argentina',
    'CL': 'Chile',
    'CO': 'Colombia',
    'PE': 'Peru',
    'FR': 'France',
    'DE': 'Germany',
    'IT': 'Italy',
    'JP': 'Japan',
    'CN': 'China',
    'IN': 'India',
    'CH': 'Switzerland'
  };
  return countryNames?.[countryCode] || 'Brazil';
};

/**
 * Gets pricing for specific region
 */
export const getRegionalPricing = (countryCode) => {
  // Brazil pricing (BRL)
  if (countryCode === 'BR') {
    return {
      currency: 'BRL',
      symbol: 'R$',
      basic: {
        amount: 59,
        formatted: 'R$ 59',
        limit: '100 alunos'
      },
      pro: {
        amount: 129,
        formatted: 'R$ 129',
        limit: '300 alunos'
      },
      unlimited: {
        amount: 179,
        formatted: 'R$ 179',
        limit: 'Ilimitado'
      }
    };
  }
  
  // International pricing (USD for all other countries)
  return {
    currency: 'USD',
    symbol: '$',
    basic: {
      amount: 79,
      formatted: '$79',
      limit: '100 students'
    },
    pro: {
      amount: 149,
      formatted: '$149',
      limit: '300 students'
    },
    unlimited: {
      amount: 199,
      formatted: '$199',
      limit: 'Unlimited'
    }
  };
};

/**
 * Gets available payment methods for specific region
 */
export const getRegionalPaymentMethods = (countryCode) => {
  const paymentMethodsMap = {
    'BR': [
      { type: 'card', label: 'Cartão de Crédito/Débito', icon: 'CreditCard', enabled: true },
      { type: 'pix', label: 'PIX', icon: 'Smartphone', enabled: true },
      { type: 'boleto', label: 'Boleto Bancário', icon: 'FileText', enabled: true }
    ],
    'US': [
      { type: 'card', label: 'Credit/Debit Card', icon: 'CreditCard', enabled: true },
      { type: 'us_bank_account', label: 'ACH Direct Debit', icon: 'Building', enabled: true }
    ],
    'MX': [
      { type: 'card', label: 'Tarjeta de Crédito/Débito', icon: 'CreditCard', enabled: true },
      { type: 'oxxo', label: 'OXXO', icon: 'Store', enabled: true }
    ],
    'GB': [
      { type: 'card', label: 'Credit/Debit Card', icon: 'CreditCard', enabled: true },
      { type: 'bacs_debit', label: 'BACS Direct Debit', icon: 'Building', enabled: true }
    ],
    'EU': [
      { type: 'card', label: 'Credit/Debit Card', icon: 'CreditCard', enabled: true },
      { type: 'sepa_debit', label: 'SEPA Direct Debit', icon: 'Building', enabled: true },
      { type: 'ideal', label: 'iDEAL', icon: 'Smartphone', enabled: true },
      { type: 'sofort', label: 'Sofort', icon: 'Zap', enabled: true }
    ],
    'IN': [
      { type: 'card', label: 'Credit/Debit Card', icon: 'CreditCard', enabled: true },
      { type: 'upi', label: 'UPI', icon: 'Smartphone', enabled: true },
      { type: 'netbanking', label: 'Net Banking', icon: 'Building', enabled: true }
    ],
    'AU': [
      { type: 'card', label: 'Credit/Debit Card', icon: 'CreditCard', enabled: true },
      { type: 'au_becs_debit', label: 'BECS Direct Debit', icon: 'Building', enabled: true }
    ]
  };

  // Check if country is in EU
  const euCountries = ['PT', 'ES', 'FR', 'DE', 'IT', 'NL', 'BE', 'AT', 'IE'];
  if (euCountries?.includes(countryCode)) {
    return paymentMethodsMap?.['EU'];
  }

  return paymentMethodsMap?.[countryCode] || [
    { type: 'card', label: 'Credit/Debit Card', icon: 'CreditCard', enabled: true }
  ];
};

export default {
  detectUserCountry,
  getRegionalPricing,
  getRegionalPaymentMethods
};